import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Search, X, CheckCircle, Clock } from "lucide-react";

import { priorityColors } from './mesaiConstants';

const ParkOncelikleriSection = ({
  // State'ler
  parkSearchTerm,
  setParkSearchTerm,
  selectedPriorityFilter,
  setSelectedPriorityFilter,
  // Data
  parks,
  filteredParks,
  priorityCounts,
  // Actions (undefined = sadece görüntüleme, gölge admin)
  onUpdateParkPriority
}) => {
  const readOnly = typeof onUpdateParkPriority !== 'function';
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h3 className="text-lg font-semibold">Park Öncelik Seviyeleri</h3>
        
        {/* Tıklanabilir Öncelik Filtreleri */}
        <div className="flex gap-2">
          {Object.entries(priorityColors).map(([key, value]) => (
            <button
              key={key}
              onClick={() => setSelectedPriorityFilter(selectedPriorityFilter === key ? null : key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                selectedPriorityFilter === key 
                  ? `${value.bg} ${value.text} ring-2 ring-offset-1 ring-${key === 'cok_yogun' ? 'red' : key === 'yogun' ? 'orange' : key === 'normal' ? 'green' : 'gray'}-400`
                  : `${value.bg} ${value.text} opacity-70 hover:opacity-100`
              }`}
            >
              {value.label} ({priorityCounts[key]})
            </button>
          ))}
          {selectedPriorityFilter && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSelectedPriorityFilter(null)}
              className="text-gray-500"
            >
              <X className="h-4 w-4 mr-1" />
              Temizle
            </Button>
          )}
        </div>
      </div>
      
      {/* Park Arama */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            placeholder="Park adı veya mahalle ile ara..."
            value={parkSearchTerm}
            onChange={(e) => setParkSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-gray-500">
          {filteredParks.length} / {parks.length} park {selectedPriorityFilter && `(${priorityColors[selectedPriorityFilter]?.label})`}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto">
        {filteredParks.map(park => {
          const priority = priorityColors[park.priority_level] || priorityColors.normal;
          return (
            <Card key={park.id} className={`${priority.border} border-2`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold">{park.name}</h4>
                    <p className="text-xs text-gray-500">{park.neighborhood}</p>
                  </div>
                  <Badge className={`${priority.bg} ${priority.text}`}>
                    {priority.label}
                  </Badge>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs">Öncelik Seviyesi</Label>
                  <SearchableSelect
                    value={park.priority_level || 'normal'}
                    onChange={readOnly ? undefined : (value) => onUpdateParkPriority(park.id, value)}
                    disabled={readOnly}
                    className={`h-8 ${readOnly ? 'opacity-80 cursor-default' : ''}`}
                    showAllOption={false}
                    placeholder="Öncelik seçin"
                    searchPlaceholder="Öncelik ara..."
                    options={[
                      { id: "cok_yogun", name: "🔴 Çok Yoğun" },
                      { id: "yogun", name: "🟠 Yoğun" },
                      { id: "normal", name: "🟢 Normal" },
                      { id: "az_yogun", name: "⚪ Az Yoğun" },
                    ]}
                  />
                  
                  <div className="flex items-center gap-4 mt-2 text-xs">
                    <div className="flex items-center gap-1">
                      {park.weekend_duty_required ? (
                        <CheckCircle className="h-3 w-3 text-green-500" />
                      ) : (
                        <Clock className="h-3 w-3 text-gray-400" />
                      )}
                      <span>Hafta Sonu</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {park.holiday_duty_required ? (
                        <CheckCircle className="h-3 w-3 text-green-500" />
                      ) : (
                        <Clock className="h-3 w-3 text-gray-400" />
                      )}
                      <span>Resmi Tatil</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ParkOncelikleriSection;
